/* eslint-disable camelcase */
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Button,
  CircularProgress,
  Divider,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { useMutation } from '@tanstack/react-query';
import { createStudy } from 'api/study';
import { CancerType } from 'interfaces/cancerType';
import { Permission } from 'interfaces/permissionOption';
import { Study } from 'interfaces/study';
import { find, map, orderBy } from 'lodash';
import React, { ReactElement, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { humanize } from 'utils/helpers';
import queryClient from 'utils/queryClient';
import { usePlatformDockerImages } from 'utils/queryHooks/usePlatformDockerImages';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useCurrentUser } from 'utils/useCurrentUser';
import { usePermissions } from 'utils/usePermissions';
import * as yup from 'yup';
import { SELF_SERVICE } from '.';
import StudyAcl from './StudyAcl';
import StudyAdmin from './StudyAdmin';
import {
  studyDialogActionButtonSx,
  StudyDialogFooter,
  StudyDialogHeader,
  studyDialogPrimaryActionSx,
} from './StudyDialogChrome';
import { PlatformImageSelect } from './StudySettings/PlatformSettings';

const UploadStudyForm = (props: IFormProps): ReactElement => {
  const { onClose, cancerTypes, isAdmin } = props;

  const { latestVersion } = usePlatformDockerImages({
    onSuccess(data) {
      // set the default docker image to the latest version if not set
      if (platformSettings?.dockerImage === '') {
        setPlatformSettings({ ...platformSettings, dockerImage: data.latestVersion ?? '' });
      }
    },
  });

  const [isSaveStudyFail, setIsSaveStudyFail] = useState(false);
  const [duringUpload, setDuringUpload] = useState(false);
  const [platformSettings, setPlatformSettings] = useState<Study['platformSettings']>({
    dockerImage: latestVersion ?? '',
  });
  const { labId } = useCurrentLabId();

  const { userId: currentUserId } = useCurrentUser();
  const [studyAdminId, setStudyAdminId] = useState(currentUserId);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<IStudy>({
    resolver: yupResolver(schema),
    defaultValues: { labId: labId, cancer_type_id: '' },
  });

  const onSubmit: SubmitHandler<IStudy> = async (data) => {
    const convertedData = {
      ...data,
      labId,
      service_type: SELF_SERVICE,
      platformSettings: platformSettings,
    };

    uploadStudy(convertedData);
  };

  const cancerTypeOptions = orderBy(
    map(cancerTypes, (cancerType) => ({
      text: cancerType.displayName === 'N/A' ? 'N/A / Mixed or Multiple Indications' : cancerType.displayName,
      value: cancerType.id.toString(),
    })),
    'text'
  );

  const onSuccess = () => {
    reset();
    setDuringUpload(false);
    onClose();
  };

  const createStudyMutation = useMutation(createStudy, {
    onSuccess: () => {
      queryClient.invalidateQueries(['studies', labId]);
    },
  });

  const uploadStudy = async (data: IStudy) => {
    setDuringUpload(true);
    setIsSaveStudyFail(false);

    try {
      await createStudyMutation.mutateAsync(data);
      onSuccess();
    } catch (err) {
      setIsSaveStudyFail(true);
      setDuringUpload(false);
    }
  };

  const { hasPermission } = usePermissions();
  const hasPermissionToEditStudyAcl = hasPermission(Permission.EditStudyAcl);
  const hasPermissionToEditPlatformSettings = hasPermission(Permission.EditPlatformSettings);

  return (
    <form data-testid="upload-study-form" onSubmit={handleSubmit(onSubmit)}>
      <Snackbar open={isSaveStudyFail} autoHideDuration={6000} message="Failed to save study, please try again" />
      <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: 'min(82vh, 920px)', overflow: 'hidden' }}>
        <StudyDialogHeader
          title="Create Study"
          subtitle="Start with a well-defined study shell for your program, indication, or analysis milestone. After opening it, you can organize cases and slides, confirm study settings, assign the right collaborators, and prepare the workspace for review and downstream analysis."
          onClose={onClose}
          sticky
        />
        <Box sx={{ px: 3, pb: 3, pt: 3, overflowY: 'auto', flex: '1 1 auto' }}>
          <Grid container direction="column" spacing={3}>
            <Grid item container spacing={2} direction="column">
              <Grid container item spacing={2}>
                <Grid item xs={6}>
                  <Controller
                    control={control}
                    name="study_name"
                    render={({ field: { onChange } }) => (
                      <TextField
                        label="Study Name"
                        {...register('study_name')}
                        onChange={onChange}
                        placeholder="Example: ADC Baseline Validation Cohort"
                        error={Boolean(errors['study_name'])}
                        helperText={
                          humanize(errors['study_name']?.message) ||
                          'Use a durable study name that makes the program, milestone, cohort, or review purpose obvious to cross-functional teams.'
                        }
                        required
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    control={control}
                    name="cancer_type_id"
                    render={({ field: { onChange, value, ref } }) => {
                      const selectedCancerTypeOption = find(
                        cancerTypeOptions,
                        (cancerTypeOption) => cancerTypeOption.value === value
                      );

                      return (
                        <Autocomplete<CancerTypeOption, false, false, false>
                          autoHighlight
                          openOnFocus
                          options={cancerTypeOptions}
                          value={selectedCancerTypeOption || null}
                          onChange={(_event, newSelectedCancerTypeOption) => {
                            onChange(newSelectedCancerTypeOption?.value || '');
                          }}
                          getOptionLabel={(cancerTypeOption) => cancerTypeOption.text}
                          isOptionEqualToValue={(cancerTypeOption, selectedCancerTypeOptionToCheck) =>
                            cancerTypeOption.value === selectedCancerTypeOptionToCheck.value
                          }
                          noOptionsText="No indications found"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              inputRef={ref}
                              label="Indication"
                              placeholder="Search and select an indication"
                              error={Boolean(errors['cancer_type_id'])}
                              helperText={
                                humanize(errors['cancer_type_id']?.message) ||
                                'Choose the disease area or indication this study workspace will govern so cases, slides, and results stay grouped in the right context.'
                              }
                              required
                            />
                          )}
                        />
                      );
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container item spacing={2}>
                <Grid item xs={6}>
                  <Controller
                    control={control}
                    name="external_study_id"
                    render={({ field: { onChange } }) => (
                      <TextField
                        label="External Study Id"
                        {...register('external_study_id')}
                        onChange={onChange}
                        placeholder="Use the source study code or sponsor identifier if one exists"
                        helperText={
                          'Optional. Use the protocol number, sponsor study identifier, or another controlled external reference your teams use across systems and reports.'
                        }
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Grid item mt={0.5}>
                <Controller
                  control={control}
                  name="description"
                  render={({ field: { onChange } }) => (
                    <TextField
                      {...register('description')}
                      onChange={onChange}
                      placeholder="Summarize the study objective, program phase, biomarker or translational question, and what this workspace is intended to evaluate, characterize, or validate across the study."
                      multiline
                      label="Description"
                      minRows={6}
                      helperText={
                        humanize(errors['description']?.message) ||
                        'A strong description captures the study rationale, development phase, scientific or clinical question, and the endpoint, biomarker, or hypothesis the team is exploring or trying to validate.'
                      }
                    />
                  )}
                />
              </Grid>
              {isAdmin && (
                <>
                  <Grid item mt={1}>
                    <Divider />
                  </Grid>
                  <Grid container item xs={12}>
                    <PlatformImageSelect
                      updatedSettings={platformSettings}
                      setUpdatedSettings={setPlatformSettings}
                      disabled={!hasPermissionToEditPlatformSettings}
                      includeDevOption={hasPermissionToEditPlatformSettings}
                      helperText={
                        hasPermissionToEditPlatformSettings
                          ? 'Select the study platform version when a specific validated image is required for this workflow.'
                          : 'The latest approved platform version is selected automatically for this study. Contact a platform administrator if a different validated version is required.'
                      }
                    />
                  </Grid>
                </>
              )}

              {hasPermissionToEditStudyAcl && (
                <Grid item container direction="column" spacing={2}>
                  <Grid item mt={1}>
                    <Divider />
                  </Grid>
                  <Grid item>
                    <Typography variant="h4">Access Settings</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                      Define who can administer the study and who should be able to open it once cases, slides, and
                      results begin to accumulate.
                    </Typography>
                  </Grid>
                  <Grid item container>
                    <Controller
                      control={control}
                      name="studyAdmins"
                      render={({ field: { onChange } }) => (
                        <StudyAdmin
                          onChange={(value) => {
                            setStudyAdminId(value);
                            onChange(value);
                          }}
                          presetStudyAdmin={currentUserId}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item container>
                    <Controller
                      control={control}
                      name="usersAcl"
                      render={({ field: { onChange } }) => (
                        <StudyAcl onChange={onChange} lockedUsers={[studyAdminId]} />
                      )}
                    />
                  </Grid>
                </Grid>
              )}
              <Grid item mt={1}>
                <Divider />
              </Grid>
              <Grid item>
                <Alert severity="info">
                  <AlertTitle>What to do after creating the study?</AlertTitle>
                  Open the study to confirm settings, assign access, and begin organizing the workspace around the cases
                  and slides you expect to manage. Cases typically represent the operational subjects or sample
                  groupings your team tracks, while slides hold the image material and results that will accumulate as
                  the study progresses. Capture whether the study supports baseline, on-treatment, post-treatment, or
                  other comparison workflows so downstream reviewers interpret the case and slide inventory correctly.
                </Alert>
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <StudyDialogFooter sticky>
          <Button
            variant="outlined"
            color="secondary"
            onClick={onClose}
            disabled={duringUpload}
            sx={studyDialogActionButtonSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            data-testid="done-button"
            disabled={duringUpload}
            type="submit"
            sx={studyDialogPrimaryActionSx}
          >
            {duringUpload ? <CircularProgress color="inherit" size={20} /> : 'Create Study'}
          </Button>
        </StudyDialogFooter>
      </Box>
    </form>
  );
};

export interface IFormProps {
  onClose: () => void;
  cancerTypes: CancerType[];
  isAdmin: boolean;
}

interface IStudy {
  study_name: string;
  description: string;
  external_study_id: string;
  cancer_type_id: string;
  labId: string;
  platformSettings: Study['platformSettings'];
  usersAcl?: string[];
  studyAdmins?: string[];
}

interface CancerTypeOption {
  text: string;
  value: string;
}

const schema = yup.object({
  study_name: yup.string().required(),
  cancer_type_id: yup.string().required('Indication is required'),
  external_study_id: yup.string(),
  description: yup.string(),
  labId: yup.string(),
  usersAcl: yup.array().of(yup.string()),
  studyAdmins: yup.array().of(yup.string()),
});

export default UploadStudyForm;
